import { useState, useRef, useEffect } from "react";
import MESSAGE_TEMPLATES from "../../api/services/MESSAGE_TEMPLATES";
import { toast } from "react-toastify";
import Spinner from "../../components/spinner";
import styles from "../../styles/_containers/_messageTemplates/templateEditorPanel.module.scss";

// ── Variable tokens available for insertion ──
const VARIABLES = [
  { token: "{{שם_לקוח}}",  label: "שם לקוח" },
  { token: "{{תאריך}}",     label: "תאריך" },
  { token: "{{שעה}}",       label: "שעה" },
  { token: "{{שירות}}",     label: "שירות" },
  { token: "{{עובד}}",      label: "עובד" },
  { token: "{{שם_עסק}}",   label: "שם העסק" },
  { token: "{{קישור}}",     label: "קישור לביטול" },
];

// Sample values for live preview
const SAMPLE_VALUES = {
  "{{שם_לקוח}}":  "דנה כהן",
  "{{תאריך}}":     "15/04/2026",
  "{{שעה}}":       "10:30",
  "{{שירות}}":     "תספורת",
  "{{עובד}}":      "מיכל",
  "{{שם_עסק}}":   "סטודיו ביוטי",
  "{{קישור}}":     "https://app.tor.digital/cancel/abc123",
};

const SMS_LIMIT = 160;
const WA_LIMIT  = 1024;

const TemplateEditorPanel = ({ template, onClose, setSuccess }) => {
  const textareaRef = useRef(null);
  const typeInfo = template?.typeInfo;

  const [channel, setChannel] = useState(template?.channel || "whatsapp");
  const [body, setBody] = useState(template?.content || "");
  const [loading, setLoading] = useState(false);

  const charLimit = channel === "sms" ? SMS_LIMIT : WA_LIMIT;
  const isOverLimit = body.length > charLimit;

  // Preview: replace tokens with sample values
  const preview = VARIABLES.reduce(
    (text, v) => text.replaceAll(v.token, `*${SAMPLE_VALUES[v.token]}*`),
    body
  );

  // Insert variable at cursor position in textarea
  const insertVariable = (token) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end   = textarea.selectionEnd;
    const newText = body.substring(0, start) + token + body.substring(end);
    setBody(newText);
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const handleSave = async () => {
    if (!body.trim()) {
      toast.error("יש להזין תוכן לתבנית");
      return;
    }

    const payload = {
      type: typeInfo?.key,
      channel,
      content: body.trim(),
    };

    setLoading(true);
    try {
      if (template?._id) {
        await MESSAGE_TEMPLATES.update(template._id, payload);
        toast.success("התבנית עודכנה בהצלחה");
      } else {
        await MESSAGE_TEMPLATES.create(payload);
        toast.success("התבנית נוצרה בהצלחה! 🎉");
      }
      setSuccess((v) => !v);
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.message || "שגיאה בשמירת התבנית");
    }
    setLoading(false);
  };

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <>
      {/* Backdrop */}
      <div className={styles.overlay} onClick={onClose} />

      {/* Panel */}
      <div className={styles.panel}>
        {/* Header */}
        <div className={styles.panelHeader}>
          <div className={styles.panelTitle}>
            <span className={styles.typeIcon}>{typeInfo?.icon}</span>
            <div>
              <h2 className={styles.typeName}>{typeInfo?.label}</h2>
              <p className={styles.typeDesc}>{typeInfo?.description}</p>
            </div>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div className={styles.panelBody}>
          {/* Channel toggle */}
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>ערוץ שליחה</label>
            <div className={styles.channelToggle}>
              <button
                className={`${styles.channelBtn} ${channel === "whatsapp" ? styles.channelBtnActive : ""}`}
                style={channel === "whatsapp" ? { background: "#25D366", borderColor: "#25D366", color: "#fff" } : {}}
                onClick={() => setChannel("whatsapp")}
              >
                💬 וואטסאפ
              </button>
              <button
                className={`${styles.channelBtn} ${channel === "sms" ? styles.channelBtnActive : ""}`}
                style={channel === "sms" ? { background: "#6366f1", borderColor: "#6366f1", color: "#fff" } : {}}
                onClick={() => setChannel("sms")}
              >
                📱 SMS
              </button>
            </div>
          </div>

          {/* Message body */}
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>תוכן ההודעה</label>
            <textarea
              ref={textareaRef}
              className={styles.textarea}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={`כתוב כאן את תוכן ה${channel === "whatsapp" ? "וואטסאפ" : "SMS"}...`}
              dir="rtl"
              rows={6}
            />
            <div className={styles.charRow}>
              <span className={`${styles.charCount} ${isOverLimit ? styles.charOver : ""}`}>
                {body.length} / {charLimit} תווים
                {isOverLimit && " — חורג מהמגבלה!"}
              </span>
            </div>
          </div>

          {/* Variable buttons */}
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>הוספת משתנה לתבנית</label>
            <div className={styles.variablesRow}>
              {VARIABLES.map((v) => (
                <button
                  key={v.token}
                  className={styles.variableBtn}
                  onClick={() => insertVariable(v.token)}
                  type="button"
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          {/* Live preview */}
          {body.trim() && (
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>תצוגה מקדימה</label>
              <div className={`${styles.phoneMockup} ${channel === "whatsapp" ? styles.phoneMockupWa : styles.phoneMockupSms}`}>
                {/* Phone header */}
                <div className={`${styles.phoneHeader} ${channel === "whatsapp" ? styles.phoneHeaderWa : styles.phoneHeaderSms}`}>
                  <div className={styles.phoneAvatar}>{channel === "whatsapp" ? "💬" : "📱"}</div>
                  <div className={styles.phoneHeaderText}>
                    <span className={styles.phoneContactName}>דנה כהן</span>
                    <span className={styles.phoneContactSub}>{channel === "whatsapp" ? "online" : "SMS"}</span>
                  </div>
                  {channel === "whatsapp" && (
                    <div className={styles.phoneIcons}>📞 ⋮</div>
                  )}
                </div>
                {/* Messages area */}
                <div className={styles.phoneBody}>
                  <div className={`${styles.previewBubble} ${channel === "whatsapp" ? styles.previewWa : styles.previewSms}`}>
                    <p className={styles.previewText}>{preview}</p>
                    <div className={styles.previewMeta}>
                      <span className={styles.previewTime}>10:30</span>
                      {channel === "whatsapp" && <span className={styles.previewTicks}>✓✓</span>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.panelFooter}>
          <button className={styles.cancelBtn} onClick={onClose}>ביטול</button>
          <button
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={loading || !body.trim() || isOverLimit}
            style={{ background: typeInfo?.color }}
          >
            {loading ? <Spinner /> : template?._id ? "שמור שינויים" : "צור תבנית"}
          </button>
        </div>
      </div>
    </>
  );
};

export default TemplateEditorPanel;
